import type { Contract, Milestone, ProjectFundingContract } from '../types';
import { contractMilestoneActionState, type MilestoneActionSide } from '../components/MilestoneTimelineActions';
import { canSignContract } from './contractSigningAvailability';
import { combinedAgreementTimeline } from './combinedAgreementTimeline';
import { contractFrameFigures } from './contractFrameTotals';
import { contractFundingConfirmed } from './timelineFunding';
import { currentContractWorkflowMilestone } from './milestoneWorkflowStatus';
import { agreementVariationNumber } from './agreementIdentity';

/** Who looks at the overview: a party (person/company), or an invited lawyer
 *  or inspector, who reads the project without payments or actions. */
export type OverviewSide = MilestoneActionSide | 'viewer';

/** A contract's state in the project overview, from data the contract frames already use. */
export type OverviewContractStatus =
  | 'inProgress' | 'awaitingPayment' | 'paymentPending' | 'signing' | 'objection' | 'conflict' | 'closed';

/** The order the status sentence lists contract states in. */
export const OVERVIEW_STATUS_ORDER: OverviewContractStatus[] = [
  'inProgress', 'conflict', 'objection', 'awaitingPayment', 'paymentPending', 'signing', 'closed'
];

// Terminology ladder (ClickUp 869fh4948): an objection (invändning) – a
// disputed stage or one being remedied – comes before a conflict. Conflict
// starts only when someone starts conflict mode, as the API's workflow status.
const CONFLICT_STATUSES = new Set(['conflict', 'external_dispute']);
const OBJECTION_STATUSES = new Set(['disputed', 'under_resolution']);
const inConflict = (milestone: Milestone) => CONFLICT_STATUSES.has(milestone.deliveryStatus ?? '')
  || (OBJECTION_STATUSES.has(milestone.deliveryStatus ?? '') && !!milestone.conflictStartedAt);
const inObjection = (milestone: Milestone) => OBJECTION_STATUSES.has(milestone.deliveryStatus ?? '');

export type OverviewNextStep =
  | { kind: 'attest'; milestone: Milestone }
  | { kind: 'remedy'; milestone: Milestone }
  | { kind: 'conflict'; milestone: Milestone }
  | { kind: 'sign'; contract: Contract }
  | { kind: 'pay'; contract: Contract; amount: number }
  | { kind: 'waitSignature' }
  | { kind: 'waitPayment' }
  /** The viewer's line: the stage the contract is at now. */
  | { kind: 'stage'; milestone: Milestone }
  | { kind: 'nothing' };

export interface OverviewContractRow {
  /** The frame's current contract (the one ProjectView renders a frame for). */
  contract: Contract;
  /** The frame's first agreement: its number and name title the frame. */
  root: Contract;
  /** The agreement in force, which is signed and paid for. */
  actionContract: Contract;
  amendmentNumbers: number[];
  status: OverviewContractStatus;
  /** Only signed contracts count toward the project totals. */
  counted: boolean;
  amount: number;
  /** What the buyer pays for the contract (after ROT). */
  payable: number;
  paidIn: number;
  paidOut: number;
  reserved: number;
  outstanding: number;
  nextSteps: OverviewNextStep[];
}

export interface OverviewTotals {
  amount: number;
  payable: number;
  paidIn: number;
  paidOut: number;
  reserved: number;
  outstanding: number;
  /** What the company is still to be paid out (after ROT). */
  remainingPayout: number;
  /** How many contracts the totals add up. */
  count: number;
}

interface OverviewInput {
  /** The contracts ProjectView renders a frame for (timelineContracts). */
  frames: Contract[];
  contracts: Contract[];
  milestonesByContract: Record<string, Milestone[]>;
  fundingContracts?: ProjectFundingContract[];
  side: OverviewSide;
  projectFunded: boolean;
  projectAccount?: boolean;
  /** Completed or externally disputed: nothing is left to do. */
  projectClosed: boolean;
}

/**
 * The project-wide status block: one row per contract frame, with the same
 * amounts as the frame's key figures, and the totals they add up to.
 */
export function projectStatusOverview({
  frames, contracts, milestonesByContract, fundingContracts, side, projectFunded, projectClosed, projectAccount
}: OverviewInput): { rows: OverviewContractRow[]; totals: OverviewTotals } {
  const allMilestones = Object.values(milestonesByContract).flat();
  const rows = frames.map((contract): OverviewContractRow => {
    const milestones = milestonesByContract[contract.id] || [];
    const combined = contract.versionStatus !== 'superseded'
      ? combinedAgreementTimeline(contract, contracts, allMilestones) : null;
    const actionContract = combined?.contract ?? contract;
    const funding = fundingContracts?.find(item => item.contractId === actionContract.id);
    const figures = contractFrameFigures({
      contract, milestones, relatedContracts: contracts, relatedMilestones: allMilestones,
      fundingContract: fundingContracts?.find(item => item.contractId === contract.id)
    });
    const payable = figures.planTotal > 0 ? figures.planTotal : figures.afterRotTotal > 0 ? figures.afterRotTotal : figures.amount;
    const plan = figures.payoutPlan.length ? figures.payoutPlan : milestones;
    const settled = !combined?.pending && figures.fullySigned && figures.planTotal > 0 && figures.paidOut >= figures.planTotal;
    const actionablePlan = combined ? combined.milestones.filter(stage => !combined.contexts[stage.id]?.pending) : plan;

    const viewer = side === 'viewer';
    // A viewer has no payment data, so payments never decide its status.
    const status: OverviewContractStatus = projectClosed || settled ? 'closed'
      : !figures.fullySigned ? 'signing'
        : plan.some(inConflict) ? 'conflict'
          : plan.some(inObjection) ? 'objection'
          : viewer || projectAccount || funding?.fundingRequirementMissingOre !== undefined ? 'inProgress'
            : funding?.status === 'pending' ? 'paymentPending'
              : figures.outstanding > 0 || funding?.status === 'unfunded' || funding?.status === 'top_up_required'
                ? 'awaitingPayment' : 'inProgress';

    const nextSteps: OverviewNextStep[] = [];
    if (side === 'viewer') {
      if (status === 'signing') nextSteps.push({ kind: 'waitSignature' });
      else if (status !== 'closed') {
        const current = currentContractWorkflowMilestone(plan);
        nextSteps.push(current && (status === 'conflict' || status === 'objection') ? { kind: 'conflict', milestone: current }
          : current ? { kind: 'stage', milestone: current } : { kind: 'nothing' });
      }
    } else if (status !== 'closed') {
      if (canSignContract(actionContract, side)) nextSteps.push({ kind: 'sign', contract: actionContract });
      if (!projectAccount && funding?.fundingRequirementMissingOre === undefined && side === 'person' && funding?.signed && (funding.canPay || funding.canResumePayment)) {
        nextSteps.push({ kind: 'pay', contract: actionContract, amount: figures.outstanding });
      }
      const { attestableMilestones, disputeDialogueMilestone, milestoneActionTarget } =
        contractMilestoneActionState(contract, actionablePlan,
          contractFundingConfirmed(contract, fundingContracts?.find(item => item.contractId === contract.id), projectFunded), side);
      if (disputeDialogueMilestone?.deliveryStatus === 'under_resolution' && milestoneActionTarget) {
        nextSteps.push({ kind: 'remedy', milestone: milestoneActionTarget });
      } else if (disputeDialogueMilestone) {
        nextSteps.push({ kind: 'conflict', milestone: disputeDialogueMilestone });
      } else {
        attestableMilestones.forEach(milestone => nextSteps.push({ kind: 'attest', milestone }));
      }
      if (!nextSteps.length) {
        nextSteps.push(status === 'signing' ? { kind: 'waitSignature' }
          : side === 'company' && (status === 'awaitingPayment' || status === 'paymentPending') ? { kind: 'waitPayment' }
            : { kind: 'nothing' });
      }
    }

    return {
      contract,
      root: combined?.root ?? contract,
      actionContract,
      amendmentNumbers: (combined?.history ?? []).filter(group => group.contract.stageVariation)
        .map(group => agreementVariationNumber(group.contract)!).sort((a, b) => a - b),
      status,
      counted: figures.fullySigned,
      amount: figures.amount,
      payable,
      paidIn: figures.paidIn,
      paidOut: figures.paidOut,
      reserved: figures.reserved,
      outstanding: figures.outstanding,
      nextSteps
    };
  });

  // A contract counts once both parties have signed it; drafts and unsigned
  // ÄTA are listed but not added up.
  const sum = (pick: (row: OverviewContractRow) => number) => rows
    .filter(row => row.counted).reduce((total, row) => total + pick(row), 0);
  return {
    rows,
    totals: {
      amount: sum(row => row.amount),
      payable: sum(row => row.payable),
      paidIn: sum(row => row.paidIn),
      paidOut: sum(row => row.paidOut),
      reserved: sum(row => row.reserved),
      outstanding: sum(row => row.outstanding),
      remainingPayout: sum(row => Math.max(0, row.payable - row.paidOut)),
      count: rows.filter(row => row.counted).length
    }
  };
}

/** How many contracts are in each state, in sentence order, skipping empty ones. */
export const overviewStatusCounts = (rows: OverviewContractRow[]) => OVERVIEW_STATUS_ORDER
  .map(status => ({ status, count: rows.filter(row => row.status === status).length }))
  .filter(entry => entry.count > 0);

/**
 * Whether a next step is an open objection (invändning, a disputed stage not
 * yet in conflict mode), which the overview shows as one notice at its stage.
 */
export const isObjectionStep = (step: OverviewNextStep): step is Extract<OverviewNextStep, { kind: 'conflict' }> =>
  step.kind === 'conflict' && step.milestone.deliveryStatus === 'disputed' && !step.milestone.conflictStartedAt;

/**
 * The stages that already have an objection notice in the overview, so a
 * silence case for the same stage is not shown as a second notice (ClickUp 869fj6n8x).
 */
export const objectionNoticeMilestoneIds = (rows: OverviewContractRow[], side: OverviewSide) => side === 'viewer'
  ? new Set<string>()
  : new Set(rows.flatMap(row => row.nextSteps.filter(isObjectionStep).map(step => step.milestone.id)));
